/**
 * ProPlusLanding — iter87.199.87
 *
 * Anon-visitor marketing page at `/rivalry-pro`. Signed-in Pro+ users
 * see the hub via ProPlusIndex — this component only renders for
 * anonymous visitors.
 *
 * iter87.199.87 — Operator hero copy pass:
 *   - Line 1 (white): `Never miss a beat.`
 *   - Line 2 (volt-blue whole span): `Run it like a boss.`
 *   - Sub-paragraph rewritten with the AI Friend one-liner.
 *
 * Color-token reminder (this codebase, tailwind.config.js):
 *   - `volt`  = #007AFF (blue)  ← used everywhere on this page
 *   - `cyber` = #FFCC00 (yellow) ← intentionally NOT used here
 */
import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Crown } from "lucide-react";
import ProPlusFeatureCarousel from "./ProPlusFeatureCarousel";
import ProPlusPromoVideo from "./ProPlusPromoVideo";
import PricingCompareTable from "./PricingCompareTable";
import TrustStrip from "./TrustStrip";

export default function ProPlusLanding() {
    return (
        <div
            className="min-h-screen bg-[#0a0a0a] text-white"
            data-testid="pro-plus-landing"
        >
            {/* ─────────  Header rail — mirrors main landing (volt tone)  ───────── */}
            <header
                className="relative z-20 flex items-center justify-between gap-2 px-4 sm:px-6 lg:px-12 py-4 sm:py-6"
                data-testid="pro-plus-landing-header"
            >
                <Link
                    to="/"
                    className="flex items-center gap-2 min-w-0 group"
                    data-testid="pro-plus-landing-brand"
                >
                    <div className="w-8 h-8 sm:w-9 sm:h-9 bg-volt border-2 border-black shadow-brutal flex items-center justify-center shrink-0">
                        <Crown className="w-4 h-4 sm:w-5 sm:h-5 text-black" strokeWidth={2.5} />
                    </div>
                    <span className="font-display font-black leading-tight tracking-tight truncate">
                        <span className="block text-[10px] sm:text-xs text-white" data-testid="pro-plus-landing-brand-line-1">
                            TINY CREW RIVALRIES
                        </span>
                        <span
                            className="block text-base sm:text-xl"
                            data-testid="pro-plus-landing-tier-pill"
                        >
                            <span className="text-white">RIVALRY</span> <span className="text-volt">PRO+</span>
                        </span>
                    </span>
                </Link>
                <Link
                    to="/rivalry-pro/sign-in"
                    data-testid="pro-plus-landing-access-cta"
                    className="brutal-btn text-[11px] sm:text-xs py-1.5 px-2.5 sm:px-3 bg-sky-400 text-black border-2 border-sky-400 hover:bg-sky-300 inline-flex items-center gap-2"
                >
                    <span className="relative flex h-2 w-2" data-testid="pro-plus-landing-access-pulse-dot">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-black opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-black"></span>
                    </span>
                    <span className="hidden sm:inline">Access Pro+</span>
                    <span className="sm:hidden">Access</span>
                </Link>
            </header>

            {/* ─────────  Hero  ───────── */}
            <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 pt-8 pb-14 sm:pt-16 sm:pb-24">
                <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight mb-6">
                    <span data-testid="pro-plus-landing-hero-line-1">Never miss a beat.</span>
                    <br />
                    <span className="text-volt" data-testid="pro-plus-landing-hero-line-2">Run it like a boss.</span>
                </h1>
                <p className="text-base text-zinc-400 max-w-xl mb-8 leading-relaxed">
                    Pro+ unlocks unlimited rivalries and five tournaments per crew —
                    plus an AI Friend who gives bad advice sometimes, because someone
                    has to. Made for admins running more than one crew, or one crew
                    that never quits.
                </p>
                <div className="flex flex-wrap items-center gap-3">
                    <Link
                        to="/rivalry-pro/sign-up"
                        data-testid="pro-plus-landing-primary-cta"
                        className="brutal-btn bg-sky-400 text-black hover:bg-sky-300 border-transparent text-sm px-6 py-3 flex items-center gap-2"
                    >
                        Start Pro+ · $5 / month <ArrowRight className="w-4 h-4" />
                    </Link>
                    <Link
                        to="/start"
                        data-testid="pro-plus-landing-secondary-cta"
                        className="brutal-btn-ghost text-sm px-5 py-3"
                    >
                        Try Free instead
                    </Link>
                </div>
                <p className="text-xs text-zinc-500 mt-4 flex items-center gap-3 flex-wrap">
                    <span className="flex items-center gap-1"><Check className="w-3 h-3 text-volt" /> Manage all your Rivalries</span>
                    <span className="flex items-center gap-1"><Check className="w-3 h-3 text-volt" /> Access to premium features</span>
                    <span className="flex items-center gap-1"><Check className="w-3 h-3 text-volt" /> Cancel anytime</span>
                </p>
            </section>

            <ProPlusPromoVideo />
            <ProPlusFeatureCarousel />
            <PricingCompareTable />
            <TrustStrip />

            {/* ─────────  Footer CTA  ───────── */}
            <section className="max-w-6xl mx-auto px-4 py-16 text-center">
                <h2 className="font-display font-black text-2xl sm:text-3xl mb-4 tracking-tight">
                    Ready when you are.
                </h2>
                <p className="text-sm text-zinc-400 mb-6 max-w-md mx-auto">
                    Five bucks a month. Keep your crews closer. Cheaper than buying them a cup of coffee. And, you can cancel whenever.
                </p>
                <Link
                    to="/rivalry-pro/sign-up"
                    data-testid="pro-plus-landing-footer-cta"
                    className="brutal-btn bg-sky-400 text-black hover:bg-sky-300 border-transparent text-sm px-6 py-3 inline-flex items-center gap-2"
                >
                    Start Pro+ · $5 / month <ArrowRight className="w-4 h-4" />
                </Link>
            </section>
        </div>
    );
}
